import React, { useState } from 'react';
import { FiCalendar, FiX } from 'react-icons/fi';

const DateRangeSelector = ({ onDateRangeChange, defaultRange = 'all' }) => {
  const [selectedRange, setSelectedRange] = useState(defaultRange);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustom, setShowCustom] = useState(false);

  // Predefined date ranges
  const predefinedRanges = [
    { key: 'all', label: 'All Time', value: null },
    { key: 'today', label: 'Today', value: 'today' },
    { key: 'yesterday', label: 'Yesterday', value: 'yesterday' },
    { key: 'thisWeek', label: 'This Week', value: 'thisWeek' },
    { key: 'lastWeek', label: 'Last Week', value: 'lastWeek' },
    { key: 'thisMonth', label: 'This Month', value: 'thisMonth' },
    { key: 'lastMonth', label: 'Last Month', value: 'lastMonth' },
    { key: 'thisYear', label: 'This Year', value: 'thisYear' },
    { key: 'lastYear', label: 'Last Year', value: 'lastYear' },
    { key: 'custom', label: 'Custom Range', value: 'custom' }
  ];

  // Calculate date ranges
  const getDateRange = (rangeType) => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const date = today.getDate();
    const day = today.getDay();

    switch (rangeType) {
      case 'today':
        return {
          startDate: new Date(year, month, date),
          endDate: new Date(year, month, date)
        };
      case 'yesterday':
        const yesterday = new Date(year, month, date - 1);
        return {
          startDate: yesterday,
          endDate: yesterday
        };
      case 'thisWeek':
        const weekStart = new Date(year, month, date - day);
        return {
          startDate: weekStart,
          endDate: today
        };
      case 'lastWeek':
        const lastWeekStart = new Date(year, month, date - day - 7);
        const lastWeekEnd = new Date(year, month, date - day - 1);
        return {
          startDate: lastWeekStart,
          endDate: lastWeekEnd
        };
      case 'thisMonth':
        return {
          startDate: new Date(year, month, 1),
          endDate: today
        };
      case 'lastMonth':
        const lastMonthStart = new Date(year, month - 1, 1);
        const lastMonthEnd = new Date(year, month, 0);
        return {
          startDate: lastMonthStart,
          endDate: lastMonthEnd
        };
      case 'thisYear':
        return {
          startDate: new Date(year, 0, 1),
          endDate: today
        };
      case 'lastYear':
        return {
          startDate: new Date(year - 1, 0, 1),
          endDate: new Date(year - 1, 11, 31)
        };
      default:
        return null;
    }
  };

  const handleRangeSelect = (rangeKey) => {
    setSelectedRange(rangeKey);
    
    if (rangeKey === 'custom') {
      setShowCustom(true);
      return;
    }
    
    setShowCustom(false);
    
    if (rangeKey === 'all') {
      onDateRangeChange(null, null);
      return;
    }

    const range = getDateRange(rangeKey);
    if (range) {
      onDateRangeChange(range.startDate, range.endDate);
    }
  };

  const handleCustomDateChange = () => {
    if (customStartDate && customEndDate) {
      onDateRangeChange(new Date(customStartDate), new Date(customEndDate));
    }
  };

  const clearCustomDates = () => {
    setCustomStartDate('');
    setCustomEndDate('');
    setSelectedRange('all');
    setShowCustom(false);
    onDateRangeChange(null, null);
  };

  // Format date for input
  const formatDateForInput = (date) => {
    if (!date) return '';
    return date.toISOString().split('T')[0];
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
      <div className="flex items-center gap-2 mb-3">
        <FiCalendar className="text-gray-500" />
        <h3 className="text-sm font-medium text-gray-700">Filter by Date Range</h3>
      </div>
      
      {/* Predefined Range Buttons */}
      <div className="flex flex-wrap gap-2 mb-3">
        {predefinedRanges.map((range) => (
          <button
            key={range.key}
            onClick={() => handleRangeSelect(range.key)}
            className={`px-3 py-1 text-xs rounded-full border transition-colors ${
              selectedRange === range.key
                ? 'bg-blue-500 text-white border-blue-500'
                : 'bg-white text-gray-600 border-gray-300 hover:border-blue-300 hover:text-blue-600'
            }`}
          >
            {range.label}
          </button>
        ))}
      </div>

      {/* Custom Date Range Inputs */}
      {showCustom && (
        <div className="border-t border-gray-200 pt-3">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">Start Date</label>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                onBlur={handleCustomDateChange}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                max={formatDateForInput(new Date())}
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">End Date</label>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                onBlur={handleCustomDateChange}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                min={customStartDate}
                max={formatDateForInput(new Date())}
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={clearCustomDates}
                className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                title="Clear custom dates"
              >
                <FiX size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeSelector;
